<template>
  <div class="min-h-screen bg-white">

    <!-- Hero Section -->
    <section class="relative pt-8 pb-20 px-4 overflow-hidden">
      <!-- Background gradient -->
      <div class="absolute inset-0 bg-gradient-to-br from-red-50 via-pink-50 to-purple-50"></div>
      
      <!-- Animated background elements -->
      <div class="absolute top-20 left-10 w-72 h-72 bg-red-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
      <div class="absolute top-40 right-10 w-72 h-72 bg-pink-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
      <div class="absolute -bottom-8 left-1/2 w-72 h-72 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>
      
      <div class="relative max-w-7xl mx-auto">
        <div class="text-center max-w-4xl mx-auto">
          <!-- Badge -->
          <div class="inline-flex items-center px-4 py-2 rounded-full bg-white/60 backdrop-blur-sm border border-gray-200 mb-8">
            <span class="relative flex h-2 w-2 mr-2">
              <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
              <span class="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
            </span>
            <span class="text-sm font-medium text-gray-700">Trusted by content creators, agencies, and teams</span>
          </div>
          
                    <h1 class="text-5xl md:text-7xl font-extrabold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-gray-900 via-red-600 to-pink-600 leading-tight">
            The All-in-One Content<br>Production Platform
          </h1>
          
          <p class="text-xl md:text-2xl mb-10 text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Plan, create, collaborate, and distribute your content—all in one place. From storyboard to social media, streamline your entire production workflow.
          </p>
          
          <div class="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <NuxtLink to="/auth/login"
              class="group bg-gradient-to-r from-red-500 to-pink-600 text-white px-8 py-4 rounded-xl text-lg font-semibold hover:shadow-2xl hover:scale-105 transition-all duration-200 flex items-center">
              Start Free Trial
              <svg class="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6"></path>
              </svg>
            </NuxtLink>
            <button
              @click="showDemoModal = true"
              class="bg-white text-gray-700 px-8 py-4 rounded-xl text-lg font-semibold hover:bg-gray-50 transition-all duration-200 border border-gray-200 flex items-center">
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"></path>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              Watch Demo
            </button>
          </div>

          <!-- Social proof -->
          <div class="mt-16 flex flex-wrap justify-center items-center gap-8 text-gray-400">
            <div class="flex items-center space-x-2">
              <div class="flex -space-x-2">
                <div class="w-8 h-8 bg-gradient-to-br from-red-400 to-pink-400 rounded-full border-2 border-white"></div>
                <div class="w-8 h-8 bg-gradient-to-br from-purple-400 to-blue-400 rounded-full border-2 border-white"></div>
                <div class="w-8 h-8 bg-gradient-to-br from-green-400 to-teal-400 rounded-full border-2 border-white"></div>
              </div>
              <span class="text-sm font-medium">Join creators today</span>
            </div>
            <div class="flex items-center space-x-1">
              <svg class="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
              </svg>
              <span class="text-sm font-medium">Loved by our community</span>
            </div>
          </div>
        </div>

        <!-- Hero Image/Video -->
        <div class="mt-16 relative">
          <div class="absolute inset-0 bg-gradient-to-r from-red-500/20 to-pink-500/20 blur-3xl"></div>
          <div class="relative rounded-2xl overflow-hidden shadow-2xl border border-gray-200">
            <video class="w-full" autoplay loop muted playsinline>
              <source src="/video_example_1.mp4" type="video/mp4">
            </video>
          </div>
        </div>
      </div>
    </section>

    <!-- Features Section -->
    <section id="features" class="py-24 px-4 bg-white">
      <div class="max-w-7xl mx-auto">
        <div class="text-center mb-16">
          <h2 class="text-4xl md:text-5xl font-bold mb-4 text-gray-900">One platform for your entire workflow</h2>
          <p class="text-xl text-gray-600 max-w-2xl mx-auto">Everything content creators, agencies, and teams need to plan, produce, and publish great content.</p>
        </div>
        
        <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          <!-- Feature 1 -->
          <div class="group relative bg-gradient-to-br from-white to-gray-50 p-8 rounded-2xl border border-gray-200 hover:border-red-300 hover:shadow-xl transition-all duration-300">
            <div class="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-red-500/10 to-pink-500/10 rounded-bl-full"></div>
            <div class="w-14 h-14 bg-gradient-to-br from-red-500 to-pink-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
              <svg class="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"></path>
              </svg>
            </div>
            <h3 class="text-xl font-semibold mb-3 text-gray-900">Content Planning</h3>
            <p class="text-gray-600 leading-relaxed">Plan your content strategy with storyboards, shot lists, and collaborative briefs. Keep your team aligned from concept to completion.</p>
          </div>

          <!-- Feature 2 -->
          <div class="group relative bg-gradient-to-br from-white to-gray-50 p-8 rounded-2xl border border-gray-200 hover:border-purple-300 hover:shadow-xl transition-all duration-300">
            <div class="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-purple-500/10 to-blue-500/10 rounded-bl-full"></div>
            <div class="w-14 h-14 bg-gradient-to-br from-purple-500 to-blue-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
              <svg class="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
              </svg>
            </div>
            <h3 class="text-xl font-semibold mb-3 text-gray-900">Centralized Upload</h3>
            <p class="text-gray-600 leading-relaxed">Upload and manage all your footage in one secure place. Share files with your team and editors without juggling multiple platforms.</p>
          </div>

          <!-- Feature 3 -->
          <div class="group relative bg-gradient-to-br from-white to-gray-50 p-8 rounded-2xl border border-gray-200 hover:border-green-300 hover:shadow-xl transition-all duration-300">
            <div class="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-green-500/10 to-teal-500/10 rounded-bl-full"></div>
            <div class="w-14 h-14 bg-gradient-to-br from-green-500 to-teal-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
              <svg class="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
              </svg>
            </div>
            <h3 class="text-xl font-semibold mb-3 text-gray-900">Hire Expert Editors</h3>
            <p class="text-gray-600 leading-relaxed">Connect with professional editors who understand your vision. Get matched with specialists perfect for your project's needs.</p>
          </div>

          <!-- Feature 4 -->
          <div class="group relative bg-gradient-to-br from-white to-gray-50 p-8 rounded-2xl border border-gray-200 hover:border-yellow-300 hover:shadow-xl transition-all duration-300">
            <div class="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-yellow-500/10 to-orange-500/10 rounded-bl-full"></div>
            <div class="w-14 h-14 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
              <svg class="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
              </svg>
            </div>
            <h3 class="text-xl font-semibold mb-3 text-gray-900">Real-Time Collaboration</h3>
            <p class="text-gray-600 leading-relaxed">Review, comment, and approve edits with your team in real-time. Streamline feedback loops and keep projects moving forward.</p>
          </div>

          <!-- Feature 5 -->
          <div class="group relative bg-gradient-to-br from-white to-gray-50 p-8 rounded-2xl border border-gray-200 hover:border-indigo-300 hover:shadow-xl transition-all duration-300">
            <div class="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-indigo-500/10 to-purple-500/10 rounded-bl-full"></div>
            <div class="w-14 h-14 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
              <svg class="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
              </svg>
            </div>
            <h3 class="text-xl font-semibold mb-3 text-gray-900">Unlimited Revisions</h3>
            <p class="text-gray-600 leading-relaxed">Request changes until your content is pixel-perfect. Our revision process makes it easy to fine-tune every detail.</p>
          </div>

          <!-- Feature 6 -->
          <div class="group relative bg-gradient-to-br from-white to-gray-50 p-8 rounded-2xl border border-gray-200 hover:border-pink-300 hover:shadow-xl transition-all duration-300">
            <div class="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-pink-500/10 to-red-500/10 rounded-bl-full"></div>
            <div class="w-14 h-14 bg-gradient-to-br from-pink-500 to-red-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
              <svg class="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"></path>
              </svg>
            </div>
            <h3 class="text-xl font-semibold mb-3 text-gray-900">Multi-Platform Publishing</h3>
            <p class="text-gray-600 leading-relaxed">Upload your finished content to multiple platforms with one click. Save hours of time with automated distribution.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Pricing Section -->
    <section id="pricing" class="py-24 px-4 bg-gradient-to-b from-gray-50 to-white">
      <div class="max-w-7xl mx-auto">
        <div class="text-center mb-16">
          <h2 class="text-4xl md:text-5xl font-bold mb-4 text-gray-900">Simple, transparent pricing</h2>
          <p class="text-xl text-gray-600 max-w-2xl mx-auto">Choose the plan that fits your content needs. Cancel anytime.</p>
        </div>
        
        <div class="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          <!-- Starter Plan -->
          <div class="relative bg-white p-8 rounded-2xl border-2 border-gray-200 hover:border-gray-300 transition-all duration-300 hover:shadow-lg">
            <div class="mb-8">
              <h3 class="text-2xl font-bold mb-2 text-gray-900">Starter</h3>
              <div class="flex items-baseline">
                <span class="text-5xl font-bold text-gray-900">$500</span>
                <span class="text-gray-600 ml-2">/month</span>
              </div>
              <p class="text-gray-600 mt-2">Perfect for getting started</p>
            </div>
            
            <ul class="space-y-4 mb-8">
              <li class="flex items-start">
                <svg class="w-6 h-6 text-green-500 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                </svg>
                <span class="text-gray-700">4 videos per month</span>
              </li>
              <li class="flex items-start">
                <svg class="w-6 h-6 text-green-500 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                </svg>
                <span class="text-gray-700">Up to 5 minutes each</span>
              </li>
              <li class="flex items-start">
                <svg class="w-6 h-6 text-green-500 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                </svg>
                <span class="text-gray-700">2-5 day delivery</span>
              </li>
              <li class="flex items-start">
                <svg class="w-6 h-6 text-green-500 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                </svg>
                <span class="text-gray-700">Basic editing & transitions</span>
              </li>
              <li class="flex items-start">
                <svg class="w-6 h-6 text-green-500 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                </svg>
                <span class="text-gray-700">3 revisions</span>
              </li>
            </ul>
            
            <NuxtLink to="/auth/login"
              class="block text-center bg-gray-900 hover:bg-gray-800 text-white px-6 py-3 rounded-xl transition-all duration-200 font-semibold">
              Get Started
            </NuxtLink>
          </div>

          <!-- Professional Plan (Most Popular) -->
          <div class="relative bg-gradient-to-br from-red-500 to-pink-600 p-8 rounded-2xl shadow-2xl transform scale-105 border-2 border-transparent">
            <div class="absolute -top-4 left-1/2 transform -translate-x-1/2">
              <span class="bg-yellow-400 text-gray-900 px-4 py-1 rounded-full text-sm font-bold text-nowrap">
                MOST POPULAR
              </span>
            </div>
            
            <div class="mb-8">
              <h3 class="text-2xl font-bold mb-2 text-white">Professional</h3>
              <div class="flex items-baseline">
                <span class="text-5xl font-bold text-white">$2500</span>
                <span class="text-white/90 ml-2">/month</span>
              </div>
              <p class="text-white/90 mt-2">For growing teams</p>
            </div>
            
            <ul class="space-y-4 mb-8">
              <li class="flex items-start">
                <svg class="w-6 h-6 text-white mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                </svg>
                <span class="text-white">10 videos per month</span>
              </li>
              <li class="flex items-start">
                <svg class="w-6 h-6 text-white mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                </svg>
                <span class="text-white">Up to 5 minutes each</span>
              </li>
              <li class="flex items-start">
                <svg class="w-6 h-6 text-white mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                </svg>
                <span class="text-white">2-3 day delivery</span>
              </li>
              <li class="flex items-start">
                <svg class="w-6 h-6 text-white mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                </svg>
                <span class="text-white">Advanced editing & effects</span>
              </li>
              <li class="flex items-start">
                <svg class="w-6 h-6 text-white mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                </svg>
                <span class="text-white">Custom branding</span>
              </li>
              <li class="flex items-start">
                <svg class="w-6 h-6 text-white mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                </svg>
                <span class="text-white">Priority support</span>
              </li>
            </ul>
            
            <NuxtLink to="/auth/login"
              class="block text-center bg-white text-red-600 hover:bg-gray-50 px-6 py-3 rounded-xl transition-all duration-200 font-semibold">
              Get Started
            </NuxtLink>
          </div>

          <!-- Enterprise Plan -->
          <div class="relative bg-white p-8 rounded-2xl border-2 border-gray-200 hover:border-gray-300 transition-all duration-300 hover:shadow-lg">
            <div class="mb-8">
              <h3 class="text-2xl font-bold mb-2 text-gray-900">Enterprise</h3>
              <div class="flex items-baseline">
                <span class="text-5xl font-bold text-gray-900">Custom</span>
              </div>
              <p class="text-gray-600 mt-2">For large organizations</p>
            </div>
            
            <ul class="space-y-4 mb-8">
              <li class="flex items-start">
                <svg class="w-6 h-6 text-green-500 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                </svg>
                <span class="text-gray-700">Unlimited videos</span>
              </li>
              <li class="flex items-start">
                <svg class="w-6 h-6 text-green-500 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                </svg>
                <span class="text-gray-700">Long form and short form videos</span>
              </li>
              <li class="flex items-start">
                <svg class="w-6 h-6 text-green-500 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                </svg>
                <span class="text-gray-700">Priority delivery</span>
              </li>
              <li class="flex items-start">
                <svg class="w-6 h-6 text-green-500 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                </svg>
                <span class="text-gray-700">Premium editing suite</span>
              </li>
              <li class="flex items-start">
                <svg class="w-6 h-6 text-green-500 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                </svg>
                <span class="text-gray-700">Dedicated editor</span>
              </li>
              <li class="flex items-start">
                <svg class="w-6 h-6 text-green-500 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                </svg>
                <span class="text-gray-700">White-label options</span>
              </li>
            </ul>
            
            <NuxtLink to="/request-demo"
              class="block text-center bg-gray-900 hover:bg-gray-800 text-white px-6 py-3 rounded-xl transition-all duration-200 font-semibold">
              Contact Founders
            </NuxtLink>
          </div>
        </div>
      </div>
    </section>

    <!-- Process Section -->
    <section id="process" class="py-24 px-4 bg-white">
      <div class="max-w-7xl mx-auto">
        <div class="text-center mb-16">
          <h2 class="text-4xl md:text-5xl font-bold mb-4 text-gray-900">How It Works</h2>
          <p class="text-xl text-gray-600 max-w-2xl mx-auto">Get started with content creation in four simple steps</p>
        </div>
        
        <div class="relative">
          <!-- Connection line -->
          <div class="hidden lg:block absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-red-200 via-pink-200 to-purple-200 transform -translate-y-1/2"></div>
          
          <div class="grid md:grid-cols-2 lg:grid-cols-4 gap-8 relative">
            <!-- Step 1: Sign Up -->
            <div class="relative text-center group">
              <div class="relative inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-red-500 to-pink-600 rounded-2xl mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
                <span class="text-3xl font-bold text-white">1</span>
                <div class="absolute inset-0 bg-white rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity"></div>
              </div>
              <h3 class="text-xl font-bold mb-3 text-gray-900">Sign Up</h3>
              <p class="text-gray-600">Create your account and choose the plan that fits your content needs</p>
            </div>

            <!-- Step 2: Onboard -->
            <div class="relative text-center group">
              <div class="relative inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-purple-500 to-blue-600 rounded-2xl mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
                <span class="text-3xl font-bold text-white">2</span>
                <div class="absolute inset-0 bg-white rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity"></div>
              </div>
              <h3 class="text-xl font-bold mb-3 text-gray-900">Onboard</h3>
              <p class="text-gray-600">Set up your workspace, invite team members, and customize your brand preferences</p>
            </div>

            <!-- Step 3: Plan -->
            <div class="relative text-center group">
              <div class="relative inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-green-500 to-teal-600 rounded-2xl mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
                <span class="text-3xl font-bold text-white">3</span>
                <div class="absolute inset-0 bg-white rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity"></div>
              </div>
              <h3 class="text-xl font-bold mb-3 text-gray-900">Plan</h3>
              <p class="text-gray-600">Upload footage, collaborate on briefs, and match with the perfect editor for your project</p>
            </div>

            <!-- Step 4: Deliver -->
            <div class="relative text-center group">
              <div class="relative inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-2xl mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
                <span class="text-3xl font-bold text-white">4</span>
                <div class="absolute inset-0 bg-white rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity"></div>
              </div>
              <h3 class="text-xl font-bold mb-3 text-gray-900">Deliver</h3>
              <p class="text-gray-600">Review edits, request revisions, and publish to all your platforms in one click</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="py-24 px-4 bg-gradient-to-br from-red-500 via-pink-600 to-purple-600 relative overflow-hidden">
      <!-- Animated background -->
      <div class="absolute inset-0">
        <div class="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full mix-blend-overlay filter blur-3xl animate-blob"></div>
        <div class="absolute bottom-0 right-0 w-96 h-96 bg-white/10 rounded-full mix-blend-overlay filter blur-3xl animate-blob animation-delay-2000"></div>
      </div>
      
      <div class="max-w-4xl mx-auto text-center relative z-10">
        <h2 class="text-4xl md:text-5xl font-bold mb-6 text-white">Ready to elevate your content?</h2>
        <p class="text-xl mb-10 text-white/90 max-w-2xl mx-auto">
          Join creators and businesses who trust Peachy Pro to streamline their content generation workflow.
        </p>
        
        <div class="flex flex-col sm:flex-row gap-4 justify-center">
          <NuxtLink to="/auth/login"
            class="group bg-white text-red-600 px-8 py-4 rounded-xl text-lg font-semibold hover:shadow-2xl hover:scale-105 transition-all duration-200 flex items-center justify-center">
            Start Free Trial
            <svg class="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6"></path>
            </svg>
          </NuxtLink>
          <a href="#pricing"
            class="bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 px-8 py-4 rounded-xl text-lg font-semibold hover:bg-white/20 transition-all duration-200">
            View Pricing
          </a>
        </div>

        <!-- Trust badges -->
        <div class="mt-12 flex flex-wrap justify-center items-center gap-8 text-white/80">
          <div class="flex items-center space-x-2">
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
            </svg>
            <span class="text-sm">Secure & Encrypted</span>
          </div>
          <div class="flex items-center space-x-2">
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
            </svg>
            <span class="text-sm">Highly Rated</span>
          </div>
          <div class="flex items-center space-x-2">
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"></path>
            </svg>
            <span class="text-sm">Quick Turnaround</span>
          </div>
        </div>
      </div>
    </section>

    <!-- Demo Video Modal -->
    <DemoVideoModal 
      :is-open="showDemoModal" 
      @close="showDemoModal = false"
    />
  </div>
</template>

<script setup>
const showDemoModal = ref(false);

useSeoMeta({
  title: 'Peachy Pro - Streamlined Content Creation for Creators and Teams',
  description: 'Peachy Pro is the ultimate platform for content creators, agencies, and teams to plan, produce, and publish high-quality content with ease. Sign up for a free trial today!',
  ogTitle: 'Peachy Pro - Streamlined Content Creation for Creators and Teams',
  ogDescription: 'Peachy Pro is the ultimate platform for content creators, agencies, and teams to plan, produce, and publish high-quality content with ease.',
  twitterCard: 'summary_large_image',
});
</script>

<style scoped>
@keyframes blob {
  0%, 100% {
    transform: translate(0, 0) scale(1);
  }
  33% {
    transform: translate(30px, -50px) scale(1.1);
  }
  66% {
    transform: translate(-20px, 20px) scale(0.9);
  }
}

.animate-blob {
  animation: blob 7s infinite;
}

.animation-delay-2000 {
  animation-delay: 2s;
}

.animation-delay-4000 {
  animation-delay: 4s;
}
</style>
